//@flow
import React, {useState, useEffect, useCallback, useMemo} from 'react';
import {makeStyles, Grid, Typography, MenuItem} from '@material-ui/core';
import {colors} from '../../../../../../../assets/styles/colors';
import {useForm} from 'react-hook-form';
import {useStoreCountry} from '../../../../../../country/utils';
import CustomSelect from '../../../../../../../elements/CustomSelect/CustomSelect';
import CustomTextField from '../../../../../../../elements/CustomTextField/CustomTextField';
import PhoneInput from '../../../../../../../elements/PhoneInput';
import CustomButton from '../../../../../../../elements/CustomButton/CustomButton';
import {
  formatStoreDeviceIdentifier,
  useWhatsappApiTemplates,
  useStoreDevices,
} from '../../../../utils';
import Collapsible from '../../../../../../../elements/Collapsible/Collapsible';
import WhatsAppTemplateRenderer from '../../../../../../../elements/whatsapp-template-renderer';
import {openConversationService} from '../../../../promises/conversation';
import {useAlert} from '../../../../../../../elements/Alert/AlertContext';
import InlineLoader from '../../../../../../../elements/LoadingSpinner/InlineLoader';

const useStyles = makeStyles(theme => ({
  root: {
    maxWidth: '450px',
    position: 'relative',
  },
  loader: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    background: 'rgba(255, 255, 255, 0.7)',
    zIndex: 1,
    top: 0,
    left: 0,
  },
  title: {
    fontWeight: 600,
    fontSize: '1.25rem',
    color: colors.grey900,
    display: 'flex',
    width: '100%',
    justifyContent: 'center',
  },
  collapsible: {
    margin: '0 !important',
  },
}));

type Props = {
  onSubmitSuccess: () => void,
};

function getErrorMessage(error: string) {
  if (error === 'CrmMessagingContactAlreadyExistError') {
    return 'El contacto ya existe';
  }

  return 'Ocurrió un error';
}

const CONVERSATION_FORM_FIELDS = {
  FIRST_NAME: 'firstName',
  LAST_NAME: 'lastName',
  IDENTIFIER: 'phoneNumber',
  TEMPLATE_ID: 'templateId',
};

export default function OpenWhatsappBusinessConversation(props: Props) {
  const {onSubmitSuccess} = props;
  const classes = useStyles();

  const {register, handleSubmit, errors, setValue} = useForm();
  const country = useStoreCountry();
  const {showAlert} = useAlert();

  const {storeDevices} = useStoreDevices();

  const connectedWhatsappApiDevices = useMemo(
    () =>
      storeDevices.filter(
        device =>
          device.state === 'CONNECTED' && device.service === 'WHATSAPP_API'
      ),
    [storeDevices]
  );

  const [deviceSelectedId, setDeviceSelectedId] = useState<string>('');
  const [templateComponents, setTemplateComponents] = useState([]);
  const {templates, isLoading} = useWhatsappApiTemplates(deviceSelectedId);

  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const deviceSelected = connectedWhatsappApiDevices.find(
    device => device.id === deviceSelectedId
  );

  useEffect(() => {
    register(CONVERSATION_FORM_FIELDS.IDENTIFIER, {
      required: 'Este campo es requerido',
    });

    register(CONVERSATION_FORM_FIELDS.TEMPLATE_ID, {
      required: 'Este campo es requerido',
    });
  }, [register]);

  useEffect(() => {
    if (connectedWhatsappApiDevices.length === 0) {
      setDeviceSelectedId('');
      return;
    }

    const stillValid = connectedWhatsappApiDevices.some(
      device => device.id === deviceSelectedId
    );
    if (stillValid) return;

    if (connectedWhatsappApiDevices.length === 1) {
      setDeviceSelectedId(connectedWhatsappApiDevices[0].id);
      return;
    }

    setDeviceSelectedId('');
  }, [connectedWhatsappApiDevices, deviceSelectedId]);

  useEffect(() => {
    setSelectedTemplateId('');
    setValue(CONVERSATION_FORM_FIELDS.TEMPLATE_ID, '');
    setTemplateComponents([]);
  }, [deviceSelectedId, setValue]);

  const selectedTemplate = useMemo(() => {
    return templates?.find(template => template.name === selectedTemplateId);
  }, [selectedTemplateId, templates]);

  const handleInputPhoneChange = (phone: string) => {
    setValue(CONVERSATION_FORM_FIELDS.IDENTIFIER, phone);
  };

  const onSubmit = data => {
    if (!selectedTemplate || !deviceSelected) {
      showAlert(
        !deviceSelected
          ? 'Selecciona un dispositivo'
          : 'Debe seleccionar un template',
        {
          type: 'error',
        }
      );
      return;
    }

    setIsSubmitting(true);
    openConversationService({
      contactInformation: {
        fullName: `${data[CONVERSATION_FORM_FIELDS.FIRST_NAME]} ${
          data[CONVERSATION_FORM_FIELDS.LAST_NAME]
        }`,
      },
      message: {
        components:
          templateComponents.length > 0 ? templateComponents : undefined,
        template: selectedTemplate.name,
        templateLang: selectedTemplate.language,
      },
      deviceId: deviceSelected.id,
      identifier: data[CONVERSATION_FORM_FIELDS.IDENTIFIER],
      provider: 'WHATSAPP_API',
    })
      .then(() => {
        onSubmitSuccess();
      })
      .catch(error => {
        console.log(error);
        const message = getErrorMessage(error?.error?.name);
        showAlert(message, {
          type: 'error',
        });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleTemplateVariablesChange = useCallback(result => {
    setTemplateComponents(result);
  }, []);

  return (
    <section className={classes.root}>
      {(isLoading || isSubmitting) && (
        <InlineLoader className={classes.loader} loading size={40} />
      )}
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography className={classes.title}>
              Nueva Conversación
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <CustomSelect
              fullWidth
              value={deviceSelectedId}
              onChange={e => {
                setDeviceSelectedId(e.target.value);
              }}
              label="Dispositivo"
              disabled={connectedWhatsappApiDevices.length === 0}
            >
              {connectedWhatsappApiDevices.map(device => (
                <MenuItem key={device.id} value={device.id}>
                  <Typography>{formatStoreDeviceIdentifier(device)}</Typography>
                </MenuItem>
              ))}
            </CustomSelect>
          </Grid>

          <Grid item xs={12} md={6}>
            <CustomTextField
              label="Nombre"
              name={CONVERSATION_FORM_FIELDS.FIRST_NAME}
              inputRef={register({
                required: 'Este campo es requerido',
              })}
              helperText={errors[CONVERSATION_FORM_FIELDS.FIRST_NAME]?.message}
              error={!!errors[CONVERSATION_FORM_FIELDS.FIRST_NAME]}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <CustomTextField
              label="Apellido"
              name={CONVERSATION_FORM_FIELDS.LAST_NAME}
              inputRef={register({
                required: 'Este campo es requerido',
              })}
              helperText={errors[CONVERSATION_FORM_FIELDS.LAST_NAME]?.message}
              error={!!errors[CONVERSATION_FORM_FIELDS.LAST_NAME]}
            />
          </Grid>
          <Grid item xs={12}>
            <PhoneInput
              defaultCountry={country}
              label="Número de teléfono"
              name="identifier"
              fullWidth
              onChange={handleInputPhoneChange}
              error={!!errors[CONVERSATION_FORM_FIELDS.IDENTIFIER]}
              helperText={errors[CONVERSATION_FORM_FIELDS.IDENTIFIER]?.message}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomSelect
              fullWidth
              value={selectedTemplateId}
              onChange={e => {
                setSelectedTemplateId(e.target.value);
                setValue(CONVERSATION_FORM_FIELDS.TEMPLATE_ID, e.target.value);
              }}
              label="Template"
              error={!!errors[CONVERSATION_FORM_FIELDS.TEMPLATE_ID]}
              helperText={errors[CONVERSATION_FORM_FIELDS.TEMPLATE_ID]?.message}
              disabled={!deviceSelectedId}
            >
              {templates.map(template => (
                <MenuItem key={template?.id} value={template?.name}>
                  <Typography>{template?.name}</Typography>
                </MenuItem>
              ))}
            </CustomSelect>
          </Grid>
          {selectedTemplate && (
            <Grid item xs={12}>
              <Collapsible
                className={classes.collapsible}
                title="Visualizar template"
              >
                <WhatsAppTemplateRenderer
                  template={selectedTemplate}
                  onChangeVariables={handleTemplateVariablesChange}
                />
              </Collapsible>
            </Grid>
          )}

          <Grid item xs={12}>
            <CustomButton
              variant="contained"
              fullWidth
              color="primary"
              type="submit"
              disabled={isSubmitting || isLoading || !deviceSelectedId}
            >
              {isSubmitting ? 'Enviando...' : 'Conversar'}
            </CustomButton>
          </Grid>
        </Grid>
      </form>
    </section>
  );
}
